import {
  ShowInfoRow,
  ShowRowWithVenue,
  ShowWithComedian,
} from "@punchup/data/legacy/shows";

import { Comedian } from "@punchup/data/legacy/comedian";
import { ShowsFilterState } from "@punchup/data/legacy/venue-manager-shows";
import { VenuePagesRow } from "@punchup/data/legacy/venue_pages";
import { VenueRow } from "@punchup/data/legacy/venues";
import { VenueManagerShowsEvents } from "@punchup/analytics/mixpanel.types";
import dayjs from "@punchup/utils/dayjs";
import { filterShows } from "@punchup/data/legacy/venue-manager-shows";
import { logException } from "@punchup/sentry";
import { trackEvent } from "@punchup/analytics";
import { v4 as uuidv4 } from "uuid";

export const generateNewShowProducers = (
  comedian: Comedian | null,
  shows: ShowWithComedian[] | ShowRowWithVenue[],
  venue: Pick<VenueRow, "id" | "name" | "city" | "state">
): Omit<ShowInfoRow, "created_at"> => {
  return {
    id: uuidv4(),
    comedian_id: comedian?.id || null,
    datetime: shows?.length
      ? shows[shows.length - 1]?.datetime
      : dayjs().add(1, "hour").minute(0).format("YYYY-MM-DDTHH:mm"),
    flags: [],
    is_sold_out: false,
    metadata_text: "",
    ticket_link: "",
    vip_ticket_link: "",
    presale_code: "",
    location: `${venue.city}, ${venue.state}`,
    venue: venue.name || "",
    venue_id: venue.id,
    title: "",
    tixologi_event_id: null,
    poster_img: null,
    published_at: null,
  };
};

export const generateNewShow = (
  comedian?: Comedian | null,
  shows?: ShowWithComedian[] | ShowRowWithVenue[],
  venuePage?: VenuePagesRow,
  venue?: Partial<VenueRow>
): Omit<ShowInfoRow, "created_at"> => {
  const location = venuePage
    ? `${venuePage.city_state}`
    : `${venue?.city}, ${venue?.state}`;
  const venueName = venuePage ? venuePage.name : venue?.name;
  const venueId = venuePage ? venuePage.location_ids?.[0] : venue?.id;

  return {
    id: uuidv4(),
    comedian_id: comedian?.id || null,
    datetime: shows?.length
      ? shows[shows.length - 1]?.datetime
      : dayjs().add(1, "hour").minute(0).format("YYYY-MM-DDTHH:mm"),
    flags: [],
    is_sold_out: false,
    metadata_text: "",
    ticket_link: "",
    vip_ticket_link: "",
    presale_code: "",
    location,
    venue: venueName || "",
    venue_id: venueId || "", // FIXME: default to the first venue available. We'll need better UI to better support this
    title: "",
    tixologi_event_id: null,
    poster_img: null,
    published_at: null,
  };
};

export const deleteShow = async (
  showId: string,
  comedianId: string | null,
  venuePageId: string
) => {
  try {
    const response = await fetch(`/api/manager/tour-dates`, {
      method: "DELETE",
      body: JSON.stringify({
        showId,
        comedianRequested: comedianId,
        venuePageRequested: venuePageId,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.error) {
      throw new Error(data.error);
    }

    trackEvent(VenueManagerShowsEvents.TOUR_DATE_DELETED, {
      showId,
      comedianId,
      venuePageId,
    });
    return { success: true };
  } catch (error) {
    trackEvent(VenueManagerShowsEvents.TOUR_DATE_DELETION_FAILED, {
      showId,
      comedianId,
      venuePageId,
      error: error instanceof Error ? error.message : String(error),
    });
    logException(
      `[tour-dates/delete] Error while deleting show, showId: ${showId}`,
      { data: `Error: ${error}` }
    );
    return { error: "Failed to delete show" };
  }
};

export const showsFilterDefaults: ShowsFilterState = {
  search: "",
  orderBy: "newest",
  dateRange: {
    start: dayjs().startOf("day").toISOString(),
    end: dayjs().add(1, "year").endOf("day").toISOString(),
  },
  state: {
    published: false,
    draft: false,
    finished: false,
  },
  tickets: {
    ticketsAdded: false,
    missingTickets: false,
  },
  lineup: {
    lineupAdded: false,
    noLineup: false,
  },
};

export { filterShows };

export interface ActiveFiltersProps {
  activeFilters: ShowsFilterState;
  clearOneFilter: (
    firstKey: keyof ShowsFilterState,
    secondKey: keyof ShowsFilterState[keyof ShowsFilterState]
  ) => void;
}
